import { inject } from 'vue';
import { FormattedSchedule } from '@/composables/schedule/use-schedule-list';
import CurrentUserInfo from '@/globals/current-user-info';

// Prefixes matching the `id` attributes used by the preview iframes rendered
// by schedule-grid-list.vue/schedule-table-list.vue.
const PREVIEW_IFRAME_ID_PREFIXES = ['scheduleGridPreview', 'scheduleTablePreview'];

/**
 * Wires up the schedule-v2 preview iframes' live status reporting:
 * - Pings each iframe once it loads (`handlePreviewLoad`) so it starts
 *   reporting its status.
 * - Pauses/resumes pinging every visible iframe when the tab becomes
 *   hidden/visible.
 *
 * @returns {{ handlePreviewLoad: (schedule: FormattedSchedule) => void }}
 */
export function useSchedulePreviewStatus () {
  const $currentUser = inject('$currentUser') as typeof CurrentUserInfo;
  /**
   * Finds the preview iframe element for a given scheduleId, regardless of
   * which list view (grid/table) is currently rendered.
   * @param {string} scheduleId
   * @returns {HTMLIFrameElement | null}
   */
  function findPreviewIframe (scheduleId: string): HTMLIFrameElement | null {
    for (const prefix of PREVIEW_IFRAME_ID_PREFIXES) {
      const el = document.getElementById(`${prefix}-${scheduleId}`);
      if (el) return el as HTMLIFrameElement;
    }
    return null;
  }

  /**
   * Posts a visibility message to a single schedule's preview iframe, mirroring
   * the legacy schedule page's `postMessageToSVG`. Used to (re)start/pause the
   * live snapshot updates the iframe reports back via `handleSVGMessage`.
   * @param {string} scheduleId
   * @param {boolean} visible
   */
  function postMessageToPreviewIframe (schedule: FormattedSchedule, visible: boolean): void {
    const iframeEl = findPreviewIframe(schedule.scheduleId);
    if (!iframeEl?.contentWindow) return;

    iframeEl.contentWindow.postMessage({ activeSchedulePreview: schedule.scheduleId, visible }, '*');

    if ($currentUser.hasGroupDebug()) console.log('update snapshot ', schedule.name);
  }

  /**
   * Re-pings every currently rendered schedule's preview iframe (e.g. after the
   * tab regains visibility) so their live status resumes updating.
   * @param {boolean} visible
   */
  function postMessageToAllVisibleSchedules (visibleItems: FormattedSchedule[], visible: boolean): void {
    if ($currentUser.hasGroupDebug()) console.log('snapshot batch update start *******')

    visibleItems.forEach((schedule) => {
      postMessageToPreviewIframe(schedule, visible);
    });

    if ($currentUser.hasGroupDebug()) console.log('snapshot batch update end *******')
  }

  /**
   * Handles a preview iframe's `load` event (bubbled up from the list
   * components as `preview-load`) by pinging it once so it starts reporting
   * its live status.
   * @param {FormattedSchedule} schedule
   */
  function handlePreviewLoad (schedule: FormattedSchedule): void {
    postMessageToPreviewIframe(schedule, true);
  }

  return {
    handlePreviewLoad,
    postMessageToAllVisibleSchedules
  };
}
